import type { CharacterCommissionStatus } from '@sorrem/shared';
import { useIntroCommission, useTourProgress } from '@sorrem-client/ui/hooks';
import { TOURS } from './tours';

/** What decides whether the intro run's HUD tour is still to come. */
export interface RunHudTourFacts {
    /** Whether the account's tour progress and the character's commissions have loaded. */
    readonly isLoaded: boolean;
    /** Whether the account has finished or skipped the run HUD tour. */
    readonly isTourDone: boolean;
    /** The character's intro commission's status, or null without one. */
    readonly introStatus: CharacterCommissionStatus | null;
}

/**
 * Whether the run HUD tour is still to come: not finished or skipped, and
 * the character's intro (where it plays) not yet done. Unknown until loaded,
 * so nothing waiting on it shows early. A character without an intro, or
 * one past it, never gets the tour, so nothing waits on it for them.
 */
export function isRunHudTourPending(facts: RunHudTourFacts): boolean {
    if (!facts.isLoaded) {
        return true;
    }
    return !facts.isTourDone && facts.introStatus !== null && facts.introStatus !== 'completed';
}

/**
 * Whether the intro run's HUD tour (#234) is still to come for this
 * account. Tour progress is an account setting, so once any character has
 * finished or skipped it, it is done for all of them.
 */
export function useIsRunHudTourPending(): boolean {
    const progress = useTourProgress('run-hud', TOURS['run-hud'].version);
    const { commission, isLoading } = useIntroCommission();
    return isRunHudTourPending({
        isLoaded: progress.isLoaded && !isLoading,
        isTourDone: progress.isDone,
        introStatus: commission?.status ?? null,
    });
}
